import LogRocket from 'logrocket';

/**
 * Centralized logging system that:
 * - Overrides native console methods globally
 * - Controls console visibility based on environment and user role
 * - Sends logs to LogRocket in production
 * - Preserves caller information for debugging
 */
class Logger {
  private environment: string;
  private isAdmin: boolean = false;
  private userInitialized: boolean = false;
  private originalConsole: {
    log: typeof console.log;
    error: typeof console.error;
    warn: typeof console.warn;
    info: typeof console.info;
    debug: typeof console.debug;
  };

  constructor() {
    this.environment = process.env.REACT_APP_ENVIRONMENT || 'staging';
    
    // Save original console methods before overriding
    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };

    // Override console methods globally
    this.overrideConsoleMethods();
  }

  /**
   * Update user context after authentication
   */
  updateUser(isAdmin: boolean) {
    this.isAdmin = isAdmin;
    this.userInitialized = true;
  }

  /**
   * Determine if logs should be visible in browser console
   */
  private shouldShowInConsole(): boolean {
    // Staging: always show
    if (this.environment === 'staging') {
      return true;
    }

    // Production: only show for admin users after auth
    if (this.environment === 'production') {
      return this.userInitialized && this.isAdmin;
    }

    // Default: show (for any other environment like 'development')
    return true;
  }

  /**
   * Determine if logs should be sent to LogRocket
   */
  private shouldSendToLogRocket(): boolean {
    // Only send to LogRocket in production
    return this.environment === 'production';
  }

  /**
   * Get caller information for better debugging
   * Adapted from original AppLog implementation
   */
  private getCallerInfo(): string {
    try {
      const stack = new Error().stack;
      if (stack) {
        const stackLines = stack.split('\n');
        // Adjust index to account for call stack depth
        // 0: Error, 1: getCallerInfo, 2: log method, 3: console override, 4: actual caller
        const callerLine = stackLines[4] || stackLines[3] || stackLines[2];
        
        if (callerLine) {
          // Parse different browser stack trace formats
          let match;
          
          // Chrome/Edge format: at functionName (file:line:column) or at file:line:column
          match = callerLine.match(/at.*?(?:\((.+?):(\d+):(\d+)\)|(\/.+?):(\d+):(\d+))/);
          if (match) {
            const file = match[1] || match[4];
            const line = match[2] || match[5];
            if (file && line) {
              const fileName = file.split('/').pop() || file.split('\\').pop();
              return `${fileName}:${line}`;
            }
          }
          
          // Firefox format: functionName@file:line:column
          match = callerLine.match(/@(.+?):(\d+):(\d+)/);
          if (match) {
            const file = match[1];
            const line = match[2];
            const fileName = file.split('/').pop() || file.split('\\').pop();
            return `${fileName}:${line}`;
          }
          
          // Safari format or fallback
          match = callerLine.match(/(\/.+?):(\d+):(\d+)/);
          if (match) {
            const file = match[1];
            const line = match[2];
            const fileName = file.split('/').pop() || file.split('\\').pop();
            return `${fileName}:${line}`;
          }
        }
      }
    } catch (error) {
      return 'unknown:0';
    }
    return 'unknown:0';
  }

  /**
   * Core logging function that handles routing
   */
  private log(level: 'log' | 'error' | 'warn' | 'info' | 'debug', ...args: any[]) {
    // Handle empty calls
    if (args.length === 0) {
      return;
    }

    const callerInfo = this.getCallerInfo();
    const formattedArgs = [`[${callerInfo}]`, ...args];

    // Send to browser console if allowed
    if (this.shouldShowInConsole()) {
      this.originalConsole[level](...formattedArgs);
    }

    // Send to LogRocket in production
    if (this.shouldSendToLogRocket()) {
      try {
        // LogRocket has different methods for different log levels
        const logMessage = args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg);
            } catch {
              return String(arg);
            }
          }
          return String(arg);
        }).join(' ');

        const fullMessage = `[${callerInfo}] ${logMessage}`;

        switch (level) {
          case 'error':
            LogRocket.captureException(new Error(fullMessage));
            break;
          case 'warn':
            LogRocket.warn(fullMessage);
            break;
          case 'info':
            LogRocket.info(fullMessage);
            break;
          case 'log':
          case 'debug':
          default:
            LogRocket.log(fullMessage);
            break;
        }
      } catch (error) {
        // Silently fail if LogRocket is not available
        this.originalConsole.error('Failed to send log to LogRocket:', error);
      }
    }
  }

  /**
   * Override native console methods
   */
  private overrideConsoleMethods() {
    console.log = (...args: any[]) => this.log('log', ...args);
    console.error = (...args: any[]) => this.log('error', ...args);
    console.warn = (...args: any[]) => this.log('warn', ...args);
    console.info = (...args: any[]) => this.log('info', ...args);
    console.debug = (...args: any[]) => this.log('debug', ...args);
  }

  /**
   * Get original console methods (for internal debugging if needed)
   */
  getOriginalConsole() {
    return this.originalConsole;
  }
}

// Create singleton instance
const loggerInstance = new Logger();

/**
 * Initialize or update logger with user context
 * Call this after user authentication to enable admin-specific logging
 */
export const initializeLogger = (user?: { protected?: { isAdmin?: boolean } } | null) => {
  if (user?.protected?.isAdmin !== undefined) {
    loggerInstance.updateUser(user.protected.isAdmin);
  }
  return loggerInstance;
};

/**
 * Legacy AppLog function for backward compatibility during migration
 * This will be removed once all AppLog calls are migrated to console.log
 * @deprecated Use console.log instead
 */
export function AppLog(...data: (any | undefined)[]) {
  console.log(...data);
}

export default loggerInstance;

